import {
	attribution,
	GROUP_LABELS,
	interruptSummary,
	laneOf,
	silentReply,
	stagesOf,
} from "@superadmin/utils/turn_breakdown";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import type { CallDumpTurn } from "@voicely/zod-schemas/call_dump.zod";

function ms(value: number | null): string {
	return value === null ? "—" : `${value}ms`;
}

/**
 * One agent turn, taken apart: which vendors served it, why it was cut if it
 * was, and which stage ate the caller's wait. See `utils/turn_breakdown.ts`
 * for why each number is computed the way it is.
 */
export function TurnBreakdown({
	turn,
	caller,
}: {
	turn: CallDumpTurn;
	/** The turn immediately before, when it is the caller's — half the marks live on it. */
	caller: CallDumpTurn | null;
}) {
	const stages = stagesOf(turn, caller);
	const { ours, vendors } = attribution(stages);
	const lanes = laneOf(turn);
	const silent = silentReply(turn);

	return (
		<Stack spacing={1.5} sx={{ py: 1 }}>
			{silent && (
				<Alert severity="error">
					This turn produced text but no audio — the caller heard silence. The synthesis vendor
					reported success and returned nothing usable.
				</Alert>
			)}

			{turn.interrupted && (
				<Alert severity={turn.interrupt?.agentWasPlaying === false ? "error" : "warning"}>
					<Typography variant="body2">{interruptSummary(turn)}</Typography>
					{turn.interrupt?.detector && (
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
							The score is in {turn.interrupt.detector}'s own unit — a probability for silero, a
							mean square for energy. Do not compare it against a reading from the other detector.
						</Typography>
					)}
				</Alert>
			)}

			{lanes.length > 0 && (
				<Stack spacing={0.5}>
					<Typography variant="caption" color="text.secondary">
						Served by — the lane as it actually ran, which a mid-call failover can change from the
						agent's configuration.
					</Typography>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						{lanes.map((entry) => (
							<Chip
								key={entry.leg}
								size="small"
								variant="outlined"
								label={`${entry.leg} · ${entry.provider}${entry.model ? ` / ${entry.model}` : ""}`}
							/>
						))}
					</Stack>
				</Stack>
			)}

			{/* The answer to "which step do we optimise", stated before the
			    breakdown rather than left to be derived from it. */}
			{(ours !== null || vendors !== null) && (
				<Box
					sx={{
						p: 1.5,
						borderRadius: 2,
						bgcolor: "surface.sunken",
						border: "1px solid",
						borderColor: "surface.border",
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 600 }}>
						{ms(ours)} of this wait was ours · {ms(vendors)} was the vendors'
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Ours is settings and handover — the utterance hold above all — and is changed with an
						environment variable. The vendors' is what inference and synthesis actually took.
					</Typography>
				</Box>
			)}

			{(["silence", "before", "behind"] as const).map((group) => (
				<Box key={group}>
					<Typography
						variant="caption"
						color="text.secondary"
						display="block"
						sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
					>
						{GROUP_LABELS[group].title}
					</Typography>
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
						{GROUP_LABELS[group].note}
					</Typography>

					<Stack spacing={1}>
						{stages
							.filter((stage) => stage.group === group)
							.map((stage) => (
								<Stack key={stage.label} direction="row" justifyContent="space-between" spacing={2}>
									<Box sx={{ minWidth: 0 }}>
										<Stack
											direction="row"
											spacing={0.75}
											alignItems="center"
											flexWrap="wrap"
											useFlexGap
										>
											<Typography variant="body2">{stage.label}</Typography>
											{/* "ours" is filled because it is the one a reader must
											    not skim past. */}
											<Chip
												size="small"
												variant={stage.owner === "ours" ? "filled" : "outlined"}
												color={stage.owner === "ours" ? "warning" : "default"}
												label={stage.owner ?? "unknown"}
												sx={{ height: 18, fontSize: 11 }}
											/>
										</Stack>
										<Typography variant="caption" color="text.secondary">
											{stage.hint}
										</Typography>
										{/* Marks ran backwards. Said explicitly, because the
										    dash it produces otherwise reads as "not measured". */}
										{stage.reversed && (
											<Typography variant="caption" color="error.main" sx={{ display: "block" }}>
												Marks out of order: the end was stamped before the start, so this stage
												cannot be measured.
											</Typography>
										)}
									</Box>
									<Typography
										variant="body2"
										sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
										// Greyed rather than a number, so a missing stage never
										// reads as a fast one.
										color={stage.value === null ? "text.disabled" : "text.primary"}
									>
										{ms(stage.value)}
									</Typography>
								</Stack>
							))}
					</Stack>
				</Box>
			))}

			<Stack direction="row" justifyContent="space-between" spacing={2}>
				<Box>
					<Typography variant="body2">Audio delivered</Typography>
					<Typography variant="caption" color="text.secondary">
						{/* Two numbers when they differ: synthesis runs ahead of
						    playback, so an interrupted turn produced more than the
						    caller heard. */}
						{turn.producedMs !== null && turn.spokenMs !== null && turn.producedMs > turn.spokenMs
							? `Heard ${ms(turn.spokenMs)} of ${ms(turn.producedMs)} synthesised — the rest was cut before it played.`
							: "What the caller actually heard for this turn."}
					</Typography>
				</Box>
				<Typography
					variant="body2"
					sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
					color={silent ? "error.main" : "text.primary"}
				>
					{ms(turn.replyAudioMs)}
				</Typography>
			</Stack>
		</Stack>
	);
}
